import { Injectable, inject } from '@angular/core';

import { MembershipApiService } from './membership/membership-api.service';
import { SystemApiService } from './system/system-api.service';
import { UserApiService } from './user/user-api.service';

/**
 * ApiService facade (Angular 18).
 *
 * Replaces the AngularJS legacy facade at:
 *   apps/b2b-ui/common/services/api/api.service.js
 *
 * Strategy
 * --------
 * The legacy facade composes ~57 sub-services (one per backend domain). We
 * migrate them incrementally:
 *
 *   - **Migrated** sub-services are inject()'d directly as Angular 18 classes:
 *       readonly user   = inject(UserApiService);
 *       readonly system = inject(SystemApiService);
 *
 *   - **Not yet migrated** sub-services are exposed as lazy getters that
 *     delegate to the AngularJS service of the same name through `$injector`.
 *     This keeps the 396 b2b-ui consumers working unchanged
 *     (`apiService.orders.list(...)` still hits the legacy implementation).
 *
 * To migrate a sub-service in a future PR:
 *   1. Create `<feature>/<feature>-api.service.ts` (Angular 18, HttpClient).
 *   2. Replace the corresponding `get xxx()` getter with `readonly xxx = inject(XxxApiService)`.
 *   3. Add a spec, run typecheck, ship.
 */
/**
 * The base endpoint that the legacy `ApiService.init()` propagated to every
 * sub-service via `subService.init(endpoint)`. We replicate that contract so
 * the still-legacy AngularJS sub-services keep generating valid URLs even
 * though the legacy facade has been removed.
 */
const LEGACY_API_BASE = '/v1/api';

/**
 * AngularJS service names that the legacy `ApiService.init()` initialised.
 * Sourced 1:1 from apps/b2b-ui/common/services/api/api.service.js (pre-migration).
 *
 * Keep this list in sync with the bridge getters below — when a sub-service
 * is migrated to Angular 18 (and stops being read through `this.bridge(...)`),
 * remove its name here so we don't pointlessly call `init` on a service we
 * no longer route through.
 */
const LEGACY_SUB_SERVICES_TO_INIT = [
  'AdministrationApiService',
  'AnalyticsApiService',
  'CartApiService',
  'ComboApiService',
  'CouponsApiService',
  'CustomFieldsApiService',
  'CustomerFeesApiService',
  'RateCardsApiService',
  'CustomerServiceApiService',
  'CustomersApiService',
  'DepartmentsApiService',
  'DiscountsApiService',
  'DonationsApiService',
  'EmailStatusApiService',
  'EntityApiService',
  'EntitlementsApiService',
  'EventApiService',
  'FinanceApiService',
  'FormApiService',
  'GlcodesApiService',
  'GroupsApiService',
  'HistoryApiService',
  'InventoryApiService',
  'InventoryB2CApiService',
  'LockApiService',
  'NavigationApiService',
  'OrdersApiService',
  'OrganizationsApiService',
  'PartnersApiService',
  'PassesApiService',
  'PermissionsApiService',
  'PhotosApiService',
  'PosApiService',
  'PriceApiService',
  'ProgramApiService',
  'RefundsApiService',
  'ReportingApiService',
  'ReservationApiService',
  'ResourceApiService',
  'RolesApiService',
  'RulesEngineApiService',
  'SettlementsApiService',
  'SubscriptionsApiService',
  'TenantApiService',
  'TerminalsApiService',
  'ThemesApiService',
  'TicketsApiService',
  'UserPreferencesApiService',
  'ProxyUserApiService',
  'WalkinsApiService',
  'LoggerApiService',
  'DedupeApiService',
  'TextVariablesApiService',
  'HouseholdApiService',
  'SfdcApiService',
] as const;

@Injectable({ providedIn: 'root' })
export class ApiService {
  // Cached `$injector` resolutions for bridged sub-services. We resolve each
  // legacy service the first time it is accessed and remember the reference so
  // we don't pay the lookup cost on every property access.
  private bridgeCache: Record<string, unknown> = {};
  // The cast to `any` keeps the TS surface small — '$injector' is provided by
  // the hybrid bootstrap and never has a real `@angular/core` symbol.
  private readonly ajs = inject('$injector' as any) as { get(name: string): unknown };

  constructor() {
    this.initLegacySubServices();
  }

  private bridge<T = unknown>(angularJsName: string): T {
    return (this.bridgeCache[angularJsName] ??= this.ajs.get(angularJsName)) as T;
  }

  /**
   * Replicates the legacy `ApiService.init()` cascade. Each AngularJS
   * sub-service has an `init(endpoint)` method that sets up its base URL
   * (e.g. `this.service = endpoint + '/users'`). Without this call, every
   * legacy sub-service would issue requests to relative paths like `/list`
   * or `/login`, which the dev server then mistakenly returns the SPA HTML
   * for and the backend rejects with 400.
   */
  private initLegacySubServices(): void {
    for (const name of LEGACY_SUB_SERVICES_TO_INIT) {
      const svc = this.bridge<{ init?: (endpoint: string) => unknown }>(name);
      if (svc && typeof svc.init === 'function') {
        svc.init(LEGACY_API_BASE);
      }
    }
  }

  // ============= Migrated sub-services (Angular 18) =============

  readonly user = inject(UserApiService);
  readonly system = inject(SystemApiService);
  readonly membership = inject(MembershipApiService);

  // ============= Bridges to AngularJS legacy =============
  // Each getter mirrors the property name on the legacy `ApiService` interface
  // (apps/b2b-ui/common/services/api/types/index.ts) and returns the AngularJS
  // service registered under the corresponding name.

  get administration() { return this.bridge('AdministrationApiService'); }
  get analytics() { return this.bridge('AnalyticsApiService'); }
  get cart() { return this.bridge('CartApiService'); }
  get combo() { return this.bridge('ComboApiService'); }
  get coupons() { return this.bridge('CouponsApiService'); }
  get customFields() { return this.bridge('CustomFieldsApiService'); }
  get customerFees() { return this.bridge('CustomerFeesApiService'); }
  get rateCards() { return this.bridge('RateCardsApiService'); }
  get customerService() { return this.bridge('CustomerServiceApiService'); }
  get customers() { return this.bridge('CustomersApiService'); }
  get departments() { return this.bridge('DepartmentsApiService'); }
  get discounts() { return this.bridge('DiscountsApiService'); }
  get donations() { return this.bridge('DonationsApiService'); }
  get emailStatus() { return this.bridge('EmailStatusApiService'); }
  get entity() { return this.bridge('EntityApiService'); }
  get entitlements() { return this.bridge('EntitlementsApiService'); }
  get event() { return this.bridge('EventApiService'); }
  get finance() { return this.bridge('FinanceApiService'); }
  get form() { return this.bridge('FormApiService'); }
  get glcodes() { return this.bridge('GlcodesApiService'); }
  get groups() { return this.bridge('GroupsApiService'); }
  get history() { return this.bridge('HistoryApiService'); }
  get inventory() { return this.bridge('InventoryApiService'); }
  get inventoryB2C() { return this.bridge('InventoryB2CApiService'); }
  get lock() { return this.bridge('LockApiService'); }
  get navigation() { return this.bridge('NavigationApiService'); }
  get orders() { return this.bridge('OrdersApiService'); }
  get organizations() { return this.bridge('OrganizationsApiService'); }
  get partners() { return this.bridge('PartnersApiService'); }
  get passes() { return this.bridge('PassesApiService'); }
  get permissions() { return this.bridge('PermissionsApiService'); }
  get photos() { return this.bridge('PhotosApiService'); }
  get pos() { return this.bridge('PosApiService'); }
  get price() { return this.bridge('PriceApiService'); }
  get program() { return this.bridge('ProgramApiService'); }
  get refunds() { return this.bridge('RefundsApiService'); }
  get reporting() { return this.bridge('ReportingApiService'); }
  get reservations() { return this.bridge('ReservationApiService'); }
  get resource() { return this.bridge('ResourceApiService'); }
  get roles() { return this.bridge('RolesApiService'); }
  get rulesEngine() { return this.bridge('RulesEngineApiService'); }
  get settlements() { return this.bridge('SettlementsApiService'); }
  get subscriptions() { return this.bridge('SubscriptionsApiService'); }
  get tenant() { return this.bridge('TenantApiService'); }
  get terminals() { return this.bridge('TerminalsApiService'); }
  get themes() { return this.bridge('ThemesApiService'); }
  get tickets() { return this.bridge('TicketsApiService'); }
  get userPreferences() { return this.bridge('UserPreferencesApiService'); }
  get userProxy() { return this.bridge('ProxyUserApiService'); }
  get walkins() { return this.bridge('WalkinsApiService'); }
  get logger() { return this.bridge('LoggerApiService'); }
  get dedupe() { return this.bridge('DedupeApiService'); }
  get textVariables() { return this.bridge('TextVariablesApiService'); }
  get household() { return this.bridge('HouseholdApiService'); }
  get sfdc() { return this.bridge('SfdcApiService'); }
}
